import { useState, useMemo } from "react";
import { cn } from "../../lib/utils";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";

export function StatusBadge({ status, className }) {
  const statusStyles = {
    // General
    active: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
    inactive: "bg-surface-elevated/50 text-muted-foreground border-border",
    
    // Project/Shift statuses
    draft: "bg-surface-elevated/50 text-muted-foreground border-border",
    published: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
    in_progress: "bg-amber-500/10 text-amber-500 border-amber-500/20",
    completed: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
    cancelled: "bg-red-500/10 text-red-400 border-red-500/20",
    
    // Staff statuses
    pending: "bg-amber-500/10 text-amber-500 border-amber-500/20",
    
    // Assignment statuses
    confirmed: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
    declined: "bg-red-500/10 text-red-400 border-red-500/20",
    no_show: "bg-red-500/10 text-red-400 border-red-500/20",
    
    // Quote statuses
    sent: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
    approved: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
    rejected: "bg-red-500/10 text-red-400 border-red-500/20",
    expired: "bg-surface-elevated/50 text-muted-foreground border-border",
    
    // Invoice statuses
    paid: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
    overdue: "bg-red-500/10 text-red-400 border-red-500/20",
    
    // Availability
    available: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
    unavailable: "bg-red-500/10 text-red-400 border-red-500/20",
    tentative: "bg-amber-500/10 text-amber-500 border-amber-500/20",
  };

  const style = statusStyles[status] || statusStyles.inactive;
  const displayStatus = status?.replace(/_/g, ' ');

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border",
        style,
        className
      )}
    >
      {displayStatus}
    </span>
  );
}

export function MetricCard({ title, value, subtitle, icon: Icon, trend }) {
  return (
    <div className="bg-card border border-white/5 p-6 rounded-sm hover:border-primary/30 transition-colors duration-300">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
            {title}
          </p>
          <p className="text-3xl font-bold font-secondary tracking-tight text-foreground">
            {value}
          </p>
          {subtitle && (
            <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
          )}
        </div>
        {Icon && (
          <div className="p-2 bg-primary/10 rounded-sm">
            <Icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
          </div>
        )}
      </div>
      {trend && (
        <div className={cn(
          "mt-3 text-xs font-medium",
          trend > 0 ? "text-emerald-500" : "text-red-400"
        )}>
          {trend > 0 ? "↑" : "↓"} {Math.abs(trend)}% from last week
        </div>
      )}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex items-center justify-between mb-8">
      <div>
        <h1 className="text-3xl font-bold font-secondary tracking-tight uppercase text-foreground">
          {title}
        </h1>
        {subtitle && (
          <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-3">{actions}</div>}
    </div>
  );
}

export function DataTable({ 
  columns, 
  data, 
  onRowClick, 
  emptyMessage = "No data found",
  // EF-FIX-CREW-ADMIN-STATUS-GROUPING-FULL-DATASET-V1: Server-side sorting support
  onSort = null,  // Callback for server-side sorting: (field, direction) => void
  sortField: externalSortField = null,  // Controlled sort field
  sortDirection: externalSortDirection = null,  // Controlled sort direction
  disableLocalSort = false  // When true, disable local sorting entirely
}) {
  // Sorting state (used only when local sorting is enabled)
  const [localSortField, setLocalSortField] = useState(null);
  const [localSortDirection, setLocalSortDirection] = useState(null); // null, "asc", "desc"

  // Use external sort state if provided, otherwise use local state
  const sortField = externalSortField !== null ? externalSortField : localSortField;
  const sortDirection = externalSortDirection !== null ? externalSortDirection : localSortDirection;

  // Handle column header click for sorting
  const handleSort = (column) => {
    if (!column.sortable) return;
    
    const field = column.sortKey || column.key;
    
    let newDirection;
    if (sortField !== field) {
      newDirection = "asc";
    } else if (sortDirection === "asc") {
      newDirection = "desc";
    } else {
      newDirection = null;
    }

    // If onSort callback provided, use server-side sorting
    if (onSort) {
      onSort(field, newDirection);
    } else if (!disableLocalSort) {
      // Use local sorting
      if (newDirection === null) {
        setLocalSortField(null);
        setLocalSortDirection(null);
      } else {
        setLocalSortField(field);
        setLocalSortDirection(newDirection);
      }
    }
  };

  // Sort data locally only if server-side sorting is not in use
  const sortedData = useMemo(() => {
    // Skip local sorting if disabled or if using server-side sorting
    if (disableLocalSort || onSort) {
      return data || [];
    }
    
    if (!data || !localSortField || !localSortDirection) return data || [];
    
    return [...data].sort((a, b) => {
      let aVal = a[localSortField];
      let bVal = b[localSortField];
      
      // Handle null/undefined
      if (aVal == null) aVal = "";
      if (bVal == null) bVal = "";
      
      // Handle dates
      if (localSortField === "date" || localSortField.includes("_at") || localSortField.includes("_date")) {
        aVal = new Date(aVal).getTime() || 0;
        bVal = new Date(bVal).getTime() || 0;
        return localSortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }
      
      // Handle numbers
      if (typeof aVal === "number" && typeof bVal === "number") {
        return localSortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }
      
      // String comparison
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: "base" });
      return localSortDirection === "asc" ? cmp : -cmp;
    });
  }, [data, localSortField, localSortDirection, disableLocalSort, onSort]);

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full data-table">
        <thead>
          <tr>
            {columns.map((col) => {
              const isActive = sortField === (col.sortKey || col.key);
              const isSortable = col.sortable !== false && col.key !== "actions";
              
              return (
                <th
                  key={col.key}
                  className={cn(
                    "px-4 py-3 text-left",
                    col.align === "right" && "text-right",
                    col.align === "center" && "text-center",
                    isSortable && "cursor-pointer hover:bg-surface-elevated/30 select-none"
                  )}
                  onClick={() => isSortable && handleSort({ ...col, sortable: true })}
                >
                  <div className={cn(
                    "flex items-center gap-1",
                    col.align === "right" && "justify-end",
                    col.align === "center" && "justify-center"
                  )}>
                    <span>{col.label}</span>
                    {isSortable && (
                      <span className="text-muted-foreground/50">
                        {isActive && sortDirection === "asc" && <ChevronUp className="h-3.5 w-3.5" />}
                        {isActive && sortDirection === "desc" && <ChevronDown className="h-3.5 w-3.5" />}
                        {!isActive && <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />}
                      </span>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, idx) => (
            <tr
              key={row.id || idx}
              onClick={() => onRowClick?.(row)}
              className={cn(onRowClick && "cursor-pointer")}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={cn(
                    "px-4 py-3",
                    col.align === "right" && "text-right",
                    col.align === "center" && "text-center"
                  )}
                >
                  {col.render ? col.render(row[col.key], row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action, variant = "default" }) {
  const variants = {
    default: "bg-surface-elevated/30",
    info: "bg-primary/5 border border-primary/10",
    warning: "bg-amber-500/5 border border-amber-500/10",
  };

  return (
    <div className={cn(
      "flex flex-col items-center justify-center py-16 text-center rounded-sm",
      variants[variant]
    )}>
      {Icon && (
        <div className="p-4 bg-surface-elevated rounded-full mb-4">
          <Icon className="w-8 h-8 text-muted-foreground" strokeWidth={1.5} />
        </div>
      )}
      <h3 className="text-lg font-semibold font-secondary uppercase tracking-wide text-foreground mb-2">
        {title}
      </h3>
      <p className="text-sm text-muted-foreground max-w-md mb-6 px-4">
        {description}
      </p>
      {action}
    </div>
  );
}

export function LoadingSpinner({ size = "md" }) {
  const sizes = {
    sm: "w-4 h-4",
    md: "w-8 h-8",
    lg: "w-12 h-12",
  };

  return (
    <div className="flex items-center justify-center p-8">
      <div
        className={cn(
          sizes[size],
          "border-2 border-primary/20 border-t-primary rounded-full animate-spin"
        )}
      />
    </div>
  );
}

export function ErrorState({ 
  title = "Failed to load data", 
  message = "There was a problem loading this page. Please try again.",
  onRetry 
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center rounded-sm bg-red-500/5 border border-red-500/10">
      <div className="p-4 bg-red-500/10 rounded-full mb-4">
        <svg className="w-8 h-8 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold font-secondary uppercase tracking-wide text-foreground mb-2">
        {title}
      </h3>
      <p className="text-sm text-muted-foreground max-w-md mb-6 px-4">
        {message}
      </p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-sm font-medium text-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
            <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
            <path d="M16 21h5v-5" />
          </svg>
          Retry
        </button>
      )}
    </div>
  );
}

export function SubmitButton({ 
  children, 
  loading = false, 
  disabled = false, 
  variant = "primary",
  className,
  ...props 
}) {
  const variants = {
    primary: "bg-primary text-primary-foreground hover:bg-primary/90",
    destructive: "bg-red-600 text-white hover:bg-red-700",
  };

  return (
    <button
      type="submit"
      disabled={loading || disabled}
      className={cn(
        "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm font-medium text-sm transition-colors",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        variants[variant],
        className
      )}
      {...props}
    >
      {loading && (
        <span className="h-4 w-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
      )}
      {children}
    </button>
  );
}

export function LockedFieldNotice({ message = "This field is locked and cannot be edited." }) {
  return (
    <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
      {message}
    </p>
  );
}

export function SkeletonRow({ columns = 5 }) {
  return (
    <tr className="animate-pulse">
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 bg-surface-elevated rounded w-3/4" />
        </td>
      ))}
    </tr>
  );
}

export function TableSkeleton({ rows = 5, columns = 5 }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full data-table">
        <thead>
          <tr>
            {Array.from({ length: columns }).map((_, i) => (
              <th key={i} className="px-4 py-3">
                <div className="h-3 bg-surface-elevated rounded w-20 animate-pulse" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <SkeletonRow key={i} columns={columns} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function formatCurrency(amount, symbol = "£") {
  if (amount === null || amount === undefined) return "-";
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(num)) return "-";
  return `${symbol}${num.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatDate(dateString) {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return String(dateString);
  }
}

export function formatDateTime(dateString) {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(dateString);
  }
}

export function formatDuration(hours) {
  if (hours === null || hours === undefined) return "-";
  const num = typeof hours === "string" ? parseFloat(hours) : hours;
  if (isNaN(num)) return "-";
  return `${num.toFixed(1)}h`;
}
